import {
  Address,
  AddressInput,
  CartState,
  Category,
  Coupon,
  CreateOrderData,
  DeliveryInterval,
  OrderReviewType,
  PaymentMethodIdEnum,
  Product,
  ProductImageTypes,
  ProductReview,
  ProductType,
  ShippingMethod,
  ShippingMethodDeliveryType,
  ShippingMethodType,
  ShippingZone,
  TOrder,
  User,
  RawCartResponse,
  SupaAdditionalFeature,
  AdditionalFeature,
  TinkoffPayment,
  TinkoffPaymentStatus,
  TinkoffGetQrResponse,
  TinkoffGetQr,
  Banner,
} from "@core/types";
import {
  AddressSuggestionEF,
  CreateFortimeOrder,
  Customer,
  FortimeCategory,
  FortimeCoupon,
  FortimeOrderWithItems,
  FortimeProduct,
  FortimeProductReview,
  OrderReviewSB,
  ShippingZonesSupaNoCode,
} from "./types";
import { ShippingMethodMethodType, UpdateCustomerAddressMutationVariables } from "../../../../generated/graphql";
import { getPlatforms } from "@ionic/react";
import { useMarketExpressDataState } from "../../../../providers/marketMode";
import { getCustomerFullAddress } from "../../../../utils";
import { TablesInsert, Tables } from "../types/supabase";
import {
  SupaBanner,
  SupaCustomerSurveyCompletionsType,
  SupaPayment,
  SupaShippingMethod,
} from "../types/supabaseFragments";
import { removeHtmlEntities, supaImage } from "./utils";

const codeBaseVersion = process.env.REACT_APP_PARHATO_CODE_VERSION!;

/**
 * @param order take a parhato order and convert to base order
 */
export function convertFortimeOrderToBaseOrder(order: FortimeOrderWithItems): TOrder {
  console.log({ order });
  const deliveryTime =
    order.delivery_interval_id.time_start && order.delivery_interval_id.time_end
      ? `${order.delivery_interval_id.time_start?.slice(0, 5)} - ${order.delivery_interval_id.time_end?.slice(0, 5)}`
      : "";

  return {
    coupons: [],
    id: order.id,
    deliveryTime,
    total: order.total,
    number: String(order.id),
    status: order.status || "",
    dateCreated: order.created_at!,
    currencySymbol: order.currency === "643" ? "₽" : order.currency,
    customerNote: order.comment || "",
    rewardPoints: order.reward_points || 0,
    paymentStatus: order.payment_status ?? "",
    deliveryDate: order.delivery_date as unknown as Date,
    discountTotal: String(order.discount_total || "0"),
    needsPayment: order.needs_payment,
    shippingTotal: String(order.shipping_total || "0"),
    lineItems: order.line_items?.map((lineItem) => {
      const image = lineItem.product_id?.images?.[0];
      return {
        parentName: lineItem.parent_name,
        id: lineItem.product_id.id,
        name: lineItem.name ?? "",
        quantity: lineItem.quantity ?? 0,
        price: String(lineItem.price ?? 0),
        total: String(lineItem.total ?? 0),
        productId: lineItem.product_id.id,
        image: {
          id: image?.id,
          src: supaImage(image?.src, "images"),
          type: ProductImageTypes.img,
        },
        // @ts-ignore
        variationId: lineItem.variation_id ?? "",
      };
    }),
    shippingLine: {
      shipId: String(order.shipping_method_id.id),
      total: String(order.shipping_total),
      methodTitle: order.shipping_method_id.title ?? "",
      methodId: String(order.shipping_method_id.id),
    },
    paymentMethod: {
      id: String(order.payment_method_id.id),
      title: order.payment_method_id.title,
      type: order.payment_method_id.type === "bacs" ? PaymentMethodIdEnum.card : PaymentMethodIdEnum.cash,
    },
    customer: {
      id: String(order.customer.id),
      // @ts-ignore
      email: order.customer.email || "",
      phone: order.customer.phone,
      lastName: order.customer.lastName,
      firstName: order.customer.firstName,
      whatsappNumber: order.customer.whatsappNumber,
    },
    address: {
      city: order.address?.city || "",
      house: order.address?.house || "",
      street: order.address?.street || "",
      latitude: order.address?.latitude || "",
      longitude: order.address?.longitude || "",
      intercom: order.address?.intercom || "",
      isFlat: order.address?.isFlat || false,
      // @ts-ignore
      floor: order.address?.floor || "",
      // @ts-ignore
      entrance: order.address?.entrance || "",
      // @ts-ignore
      apartmentNumber: order.address?.apartmentNumber || "",
    },
  };
}

/**
 * Change the created order to the parhato type
 * @param order take a base order without id and convert to parhato order
 */
export function convertCreateOrderToFortime(order: CreateOrderData): Partial<CreateFortimeOrder> {
  const metaData = [
    {
      key: "_order_whatsapp",
      value: order.customer?.whatsappNumber,
    },
    {
      key: "app_info",
      value: `from parhato app v${codeBaseVersion}; ${getPlatforms()}`,
    },
    {
      key: "kv",
      value: order.address?.isFlat ? "Квартира" : "Частный дом",
    },
    {
      key: "domofon",
      value: order.address?.intercom || "",
    },
    {
      key: "billing_address_3",
      value: order.address?.apartmentNumber || "",
    },
    {
      key: "billing_address_4",
      value: order?.address?.entrance || "",
    },
    {
      key: "search_address",
      value: order.address?.fullAddress || "",
    },
    {
      key: "billing_address_5",
      value: order.address?.floor || "",
    },
    {
      key: "longitude",
      value: order.address?.longitude || "",
    },
    {
      key: "latitude",
      value: order.address?.latitude || "",
    },
  ];

  if (order.deliveryDate) {
    metaData.push(
      {
        key: "pi_system_delivery_date",
        value:
          order.deliveryDate!.getFullYear() +
          "/" +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(order.deliveryDate!.getDate()).padStart(2, "0"),
      },
      {
        key: "pi_delivery_date",
        value:
          String(order.deliveryDate!.getDate()).padStart(2, "0") +
          "." +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "." +
          order.deliveryDate!.getFullYear(),
      },
      {
        key: "pi_delivery_type",
        value: "delivery",
      },
      {
        key: "pi_delivery_time",
        value: order.deliveryTime,
      },
    );
  }

  if (useMarketExpressDataState.getState().mode === "express") {
    metaData.push({
      key: "parhato_delivery_type",
      value: "express",
    });
  }

  return {
    customer_id: Number(order.customer?.id!),

    line_items: order.lineItems
      ? order.lineItems.map((lineItem) => {
          const convertedItem = {
            name: lineItem.name,
            total: lineItem.total,
            price: lineItem.price,
            product_id: lineItem.id,
            quantity: lineItem.quantity,
            variation_id: lineItem.variationId ?? "0",
            unit_name: lineItem.unitName ?? "шт",
          };

          console.log("🔄 [4time converter] Converting lineItem to 4time format:", {
            originalLineItem: lineItem,
            convertedItem: convertedItem,
            unitName: lineItem.unitName,
            unit_name: convertedItem.unit_name,
          });

          return convertedItem;
        })
      : [],

    status: order.status,
    customer_note: order.customerNote,
    shipping_lines: [
      {
        method_id: order.shippingLine.methodId!,
        method_title: order.shippingLine.methodTitle!,
        total: order.shippingLine.total ?? "0.00",
      },
    ],
    payment_method: order.paymentMethod.id,
    payment_method_title: order.paymentMethod.title,

    meta_data: metaData,

    billing: {
      notice: "",
      company: "",
      country: "",
      billing_country: "",

      phone: order?.customer?.phone ?? "",
      email: order?.customer?.email ?? "",

      last_name: order?.customer?.lastName ?? "",
      first_name: order?.customer?.firstName ?? "",

      billing_city: order?.address?.city ?? "",
      billing_address_2: order?.address?.house ?? "",
      billing_address_1: order?.address?.street ?? "",
      billing_address_5: order?.address?.floor ?? "",
      billing_address_4: order?.address?.entrance ?? "",
      billing_address_3: order?.address?.apartmentNumber ?? "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",
      kv: order?.address?.apartmentNumber ?? "",
      postcode: "",
      domofon: order?.address?.intercom ?? "",

      state: order?.address?.city ?? "",
      latitude: order?.address?.latitude ?? "",
      longitude: order?.address?.longitude ?? "",
      search_address: order?.address?.fullAddress ?? "",
    },
    shipping: {
      company: "",
      country: "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",

      shipping_phone: order?.customer?.whatsappNumber ?? "",
      first_name: order?.customer?.firstName ?? "",
      last_name: order?.customer?.lastName ?? "",
      phone: order?.customer?.phone ?? "",

      postcode: "",
      state: order?.address?.city ?? "",
    },
    coupon_lines: order.coupons?.map(({ code }) => ({ code })),
    reward_points: order.rewardPoints,
    cart_total: order.total,
  };
}

export const convertAddressSuggestion = (address: AddressSuggestionEF) => {
  return {
    area: address.area,
    country: address.country,
    province: address.province,
    locality: address.locality,
    text: address.text,
    street: address.street,
    house: address.house,
    point: address.point,
    location: address.location,
    postcode: "",
    district: "",
  };
};

type NewShippingMethod = Omit<ShippingMethod, "maxDeliveryDays" | "minDeliveryDays" | "order">;

const shippingMethodDeliveryTypeMap: Record<ShippingMethodMethodType, ShippingMethodDeliveryType> = {
  [ShippingMethodMethodType.Default]: ShippingMethodDeliveryType.default,
  [ShippingMethodMethodType.NextDay]: ShippingMethodDeliveryType.nextDay,
};

export const convertShippingMethod = (shippingMethod: SupaShippingMethod) => {
  const toCamelCase = (str: string) =>
    str
      .toLowerCase()
      .split("_")
      .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join("");

  const camelCaseCode = toCamelCase(shippingMethod.code);
  const methodType = ShippingMethodType[camelCaseCode as keyof typeof ShippingMethodType];

  return {
    methodDeliveryType: shippingMethodDeliveryTypeMap.DEFAULT,
    methodType,
    id: String(shippingMethod.id),
    methodId: String(shippingMethod.id),
    description: shippingMethod.description,
    enabled: shippingMethod.is_enabled,
    cost: shippingMethod.cost ? shippingMethod.cost : undefined,
    title: shippingMethod.title,
    minAmount: shippingMethod.min_amount ?? "",
  } as NewShippingMethod;
};

export const convertShippingZone = (shippingZone: ShippingZonesSupaNoCode): ShippingZone => {
  return {
    id: String(shippingZone.id),
    name: shippingZone.name,
  };
};

export function convertFortimeUserToUser(user: Customer): User {
  return {
    id: String(user.id),
    firstName: user.first_name,
    lastName: user.last_name,
    phone: user.billing.phone,
    email: user.email,
    userName: user.username,
    token: "",
  };
}

export function convertFortimeCoupon(coupon: FortimeCoupon): Coupon {
  return {
    id: String(coupon.id),
    code: coupon.code,
    discountAmount: parseFloat(coupon.amount),
    discountType: coupon.discount_type,
    minimumAmount: parseFloat(coupon.minimum_amount),
    maximumAmount: parseFloat(coupon.maximum_amount),
    dateExpires: coupon.date_expires,
    individualUse: coupon.individual_use,
    freeShipping: coupon.free_shipping,
    excludeSaleItems: coupon.exclude_sale_items,
    productIds: coupon.product_ids,
    excludedProductIds: coupon.excluded_product_ids,
    productCategories: coupon.product_categories,
    excludedProductCategories: coupon.excluded_product_categories,
  };
}

export const convertFortimeProduct = (fortimeProduct: FortimeProduct): Product => {
  const images =
    fortimeProduct.images?.map((img) => ({
      id: img?.id,
      src: supaImage(img.src, "images"),
      type: ProductImageTypes.img,
      preview_src: supaImage(img.preview_src, "images_preview"),
    })) || [];

  return {
    id: String(fortimeProduct.id),
    name: fortimeProduct.name,
    price: fortimeProduct.price,
    onSale: fortimeProduct.on_sale,
    stockStatus: fortimeProduct.stock_status,
    description: fortimeProduct.description,
    attributes: fortimeProduct.attributes?.map((attr) => ({
      ...attr,
      id: String(attr.id),
    })),
    categories: fortimeProduct.categories?.map((category) => ({
      ...category,
      id: String(category.id),
    })),
    stockQuantity: fortimeProduct.stock_quantity,
    type: fortimeProduct.type as ProductType,
    purchasable: fortimeProduct.purchasable,
    images,
    soldIndividually: fortimeProduct.sold_individually,
    groupedProducts: fortimeProduct.grouped_products?.map((id) => String(id)),
    priceHtml: fortimeProduct.price_html,
    variations: fortimeProduct.variations?.map((id) => String(id)),
    averageRating: fortimeProduct.average_rating,
    dateCreated: fortimeProduct.date_created,
    defaultAttributes: fortimeProduct.default_attributes,
    parentId: String(fortimeProduct.parent_id),
    discountPrice: null,
    weight: fortimeProduct.weight || "0",
    regularPrice: fortimeProduct.regular_price,
    parentName: fortimeProduct.parent_name,
  };
};

export const convertFortimeCategory = (fortimeCategory: FortimeCategory): Category => {
  const imageData = fortimeCategory.image?.[0];

  const image = imageData?.src
    ? {
        src: supaImage(imageData.src, "images_categories"),
        alt: imageData.name,
        id: imageData.id,
        name: imageData.name,
      }
    : undefined;

  return {
    id: String(fortimeCategory.category_id),
    description: fortimeCategory.description,
    parent: fortimeCategory.parent_category_id ?? "0",
    name: removeHtmlEntities(fortimeCategory.category_name ?? ""),
    image: image,
  };
};

export const convertFortimeReview = (review: FortimeProductReview): ProductReview => {
  return {
    id: String(review.id),
    review: review.review,
    dateCreated: review.date_created,
    fullName: review.reviewer,
    rating: review.rating,
  };
};

export const convertAddressInputCreateForSupa = (
  customerAddress: AddressInput,
  userId: User["id"],
): TablesInsert<"customer_addresses"> => {
  const location = customerAddress.longitude ? `POINT(${customerAddress.longitude} ${customerAddress.latitude})` : null;

  return {
    customer_id: Number(userId),
    apartment_number: customerAddress.apartmentNumber ?? "",
    city: customerAddress.city ?? "",
    entrance: customerAddress.entrance ?? "",
    floor: customerAddress.floor ?? "",
    intercom: customerAddress.intercom ?? "",
    is_flat: customerAddress.isFlat ?? false,
    location: location,
    state: customerAddress.state ?? null,
    street: customerAddress.street ?? null,
    house: customerAddress.house ?? null,
  };
};

export const convertAddressUpdateForSupa = (
  customerAddress: Address,
  userId: User["id"],
): TablesInsert<"customer_addresses"> => {
  return {
    customer_id: Number(userId),
    apartment_number: customerAddress.apartmentNumber ?? "",
    city: customerAddress.city ?? "",
    entrance: customerAddress.entrance ?? "",
    floor: customerAddress.floor ?? "",
    intercom: customerAddress.intercom ?? "",
    is_flat: customerAddress.isFlat ?? false,
    location: `POINT(${customerAddress.longitude} ${customerAddress.latitude})`,
    state: customerAddress.state ?? null,
    street: customerAddress.street ?? null,
    house: customerAddress.house ?? null,
  };
};

export const convertSupaAddress = (customerAddress: Tables<"customer_addresses">): Address => {
  const getCustomerFullAddressPayload = {
    street: customerAddress.street ?? "",
    apartmentNumber: customerAddress.apartment_number ?? "",
    city: customerAddress.city ?? "",
    house: customerAddress.house ?? "",
    isFlat: customerAddress.is_flat,
  };

  const fullAddress = getCustomerFullAddress(getCustomerFullAddressPayload);
  const latitude = typeof customerAddress.latitude === "number" ? String(customerAddress.latitude) : undefined;
  const longitude = typeof customerAddress.longitude === "number" ? String(customerAddress.longitude) : undefined;

  return {
    latitude,
    longitude,
    fullAddress,
    city: customerAddress.city ?? "",
    floor: customerAddress.floor ?? "",
    state: customerAddress.state ?? "",
    house: customerAddress.house ?? "",
    entrance: customerAddress.entrance ?? "",
    intercom: customerAddress.intercom ?? "",
    isFlat: customerAddress.is_flat ?? false,
    street: customerAddress.street ?? "null",
    id: customerAddress.id ? String(customerAddress.id) : "",
    apartmentNumber: customerAddress.apartment_number ?? "",
  };
};

export const convertCustomerUpdateAddressPayload = (
  customerAddress: Address,
): UpdateCustomerAddressMutationVariables["address"] => {
  return {
    apartmentNumber: customerAddress.apartmentNumber!,
    city: customerAddress.city!,
    entrance: customerAddress.entrance!,
    floor: customerAddress.floor!,
    house: customerAddress.house!,
    intercom: customerAddress.intercom || "",
    isFlat: customerAddress.isFlat ?? false,
    state: customerAddress.state!,
    street: customerAddress.street!,
    latitude: customerAddress.latitude,
    longitude: customerAddress.longitude,
  };
};

export const convertSupaDeliveryInterval = (interval: Tables<"delivery_intervals">): DeliveryInterval => {
  return {
    id: String(interval.id),
    text: `${interval.time_start} - ${interval.time_end}`,
  };
};

export const convertSupaCustomerSurvey = ({
  id,
  answers,
  created_at,
  user_phone,
}: SupaCustomerSurveyCompletionsType): SupaCustomerSurveyCompletionsType => {
  return {
    id,
    answers,
    created_at,
    user_phone,
  };
};

export const convertSupaReview = (review: Tables<"product_reviews">): ProductReview => {
  return {
    id: String(review.id),
    review: review.review,
    dateCreated: review.created_at,
    fullName: review.reviewer,
    rating: review.rating,
  };
};

export const convertOrderReviewSB = (review: OrderReviewType): Omit<OrderReviewSB, "id" | "created_at"> => {
  return {
    delivery_speed: review.deliverySpeed!,
    driver_courtesy: review.driverCourtesy!,
    product_quality: review.productQuality,
    assembly_quality: review.assemblyQuality!,
    customer_note: review.customerNote ?? "",
    order_id: Number(review.orderId),
  };
};

export const convertSBOrderReview = (review: OrderReviewSB): OrderReviewType => {
  return {
    deliverySpeed: review.delivery_speed,
    driverCourtesy: review.driver_courtesy,
    productQuality: review.product_quality,
    assemblyQuality: review.assembly_quality,
    customerNote: review.customer_note ?? "",
    orderId: String(review.order_id),
  };
};

export const convertSupaCartToCart = (cartFromSupa: RawCartResponse): CartState => {
  return {
    cartTotalValue: cartFromSupa.total,
    cartSubtotalValue: cartFromSupa.total + cartFromSupa.reward_points,
    cartProducts: cartFromSupa.products,
    cartProductsMap: cartFromSupa.products.reduce(
      (acc, product) => {
        acc[product.id] = product;
        return acc;
      },
      {} as CartState["cartProductsMap"],
    ),
    coupons: cartFromSupa.coupons,
    rewardPoints: cartFromSupa.reward_points,
    soldOutProducts: cartFromSupa.sold_out_products,
    unavailableProducts: cartFromSupa.unavailable_products,
    updatedAt: cartFromSupa.updated_at,
  };
};

export const convertCartToSupaCart = (cart: CartState): RawCartResponse => {
  return {
    total: cart.cartSubtotalValue,
    products: cart.cartProducts,
    coupons: cart.coupons,
    reward_points: cart.rewardPoints,
    sold_out_products: cart.soldOutProducts,
    unavailable_products: cart.unavailableProducts,
    updated_at: cart.updatedAt,
  };
};

export const convertAdditionalFeature = (additionalFeature: SupaAdditionalFeature): AdditionalFeature => {
  return {
    additionalFeature: additionalFeature.additional_feature,
    count: additionalFeature.count,
  };
};

export const convertTinkoffPayment = (payment: SupaPayment): TinkoffPayment => {
  return {
    total: payment.total,
    createdAt: payment.created_at,
    extra: payment.extra,
    id: payment.id,
    orderId: payment.order_id,
    customerId: payment.customer_id,
    providerPaymentId: payment.provider_payment_id ?? 0,
    providerPaymentUrl: payment.provider_payment_url ?? "",
    status: payment.status as TinkoffPaymentStatus,
  };
};

export const convertTinkoffGetQr = (payment: TinkoffGetQrResponse): TinkoffGetQr => {
  return {
    terminalKey: payment.TerminalKey,
    orderId: payment.OrderId,
    success: payment.Success,
    status: payment.Status as TinkoffPaymentStatus,
    paymentId: payment.PaymentId,
    errorCode: payment.ErrorCode,
    message: payment.Message,
    amount: payment.Amount,
    paymentURL: payment.Data,
  };
};

export const convertSupaBannerToBanner = (banner: SupaBanner): Banner => {
  return {
    id: String(banner.id),
    imageSrc: supaImage(banner.image_url, "banners"),
    link: banner.link,
    type: banner.type,
  };
};
